'use client';

import { Suspense, useEffect } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { INQ_ID_SESSION_STORAGE_KEY } from '@/constants/inq-id';
import { isValidInquiryLeadId } from '@/utils/is-valid-inquiry-lead-id';
import { setSessionStorageItem } from '@/utils/session-storage/safe-session-storage';

/** App Router paths `/[brand]/book` and `/[brand]/book/...` — keep `inq_id` in the URL for RSC searchParams. */
const BRAND_BOOK_ROUTE = /^\/[^/]+\/book(\/|$)/;

const isBrandBookRoute = (pathname: string) => BRAND_BOOK_ROUTE.test(pathname);

const InqIdUrlCaptureInner = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const fromUrl = searchParams.get('inq_id')?.trim();
    if (!fromUrl || !isValidInquiryLeadId(fromUrl)) {
      return;
    }

    setSessionStorageItem(INQ_ID_SESSION_STORAGE_KEY, fromUrl);

    if (isBrandBookRoute(pathname)) {
      return;
    }

    const next = new URLSearchParams(searchParams.toString());
    next.delete('inq_id');
    const qs = next.toString();
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const path = qs ? `${pathname}?${qs}` : pathname;
    router.replace(`${path}${hash}`, { scroll: false });
  }, [pathname, router, searchParams]);

  return null;
};

/**
 * When mounted (listing detail): if `inq_id` is present and valid, saves it to sessionStorage (overwriting any prior value).
 * On `/[brand]/book` routes, the query is left intact so the server can read `inq_id`. Else it is stripped
 * from the URL (hash preserved).
 */
export const InqIdUrlCapture = () => (
  <Suspense fallback={null}>
    <InqIdUrlCaptureInner />
  </Suspense>
);
